/**
 * The product's stacking order, in one place.
 *
 * **Every surface that portals to the document root reads this.** Before it existed, each one
 * picked its own number and the order was whatever the numbers happened to be: the upload dock
 * claimed `z-[120]`, every floating panel sat at `z-50`, and a classification dropdown opened
 * inside the dock rendered BEHIND it. That is invisible until somebody opens the one control
 * that lands on the one surface that outbid it.
 *
 * The rule the scale encodes: **a floating panel always sits above the surface that opened
 * it.** A dropdown, a menu, a calendar and a tooltip are transient things a reader summoned,
 * so they are the topmost layers by construction rather than by out-numbering whatever is
 * beneath them this month.
 *
 * ```
 *   TOOLTIP    an explanation of whatever is under the pointer — above everything
 *   PANEL      a dropdown, menu or calendar, summoned from any surface below
 *   TOAST      the outcome of a write, over whatever the reader is looking at
 *   DOCK       the upload queue: outlives a modal, and a reader keeps working under it
 *   MODAL      a dialog and its backdrop
 *   OVERLAY    the sidebar flyout and other app-shell chrome
 * ```
 *
 * **Use the class, never a raw `z-*`.** A number typed at a call site is a number that has no
 * opinion about what it is supposed to be above, which is exactly how this broke.
 */
export const LAYER = {
  /** App-shell chrome that floats over the page — the sidebar flyout. */
  OVERLAY: 'z-[80]',

  /** A dialog and its backdrop. */
  MODAL: 'z-[100]',

  /**
   * The upload dock.
   *
   * ABOVE a modal deliberately: the queue outlives whatever opened it, and a reader minimises
   * it and keeps working. A dock behind a dialog would be a progress bar nobody can see.
   */
  DOCK: 'z-[120]',

  /**
   * A toast — what became of the write the reader just made.
   *
   * ABOVE the modal and the dock, because a refusal raised by a dialog that stays open is
   * exactly the message that must not be covered by the thing that raised it.
   *
   * BELOW a panel, because a panel is something the reader SUMMONED and a toast is something
   * the product PUSHED: a dropdown they opened stays readable over a notice that arrived on
   * its own. They do not normally collide anyway — the stack is bottom-CENTRE, which is also
   * what keeps it clear of the dock at `bottom-4 right-4`.
   */
  TOAST: 'z-[130]',

  /**
   * Any floating panel — `<Select>`, `<MultiSelect>`, `<Menu>`, `<Calendar>`, `<DateField>`.
   *
   * Above the dock, because a panel is opened FROM one of the surfaces below and must be
   * readable over whichever it was. This is the layer the classification dropdown was missing.
   */
  PANEL: 'z-[140]',

  /**
   * A tooltip, which explains whatever is under the pointer — including a control inside a
   * panel. Topmost, and it never receives pointer events, so nothing is trapped beneath it.
   */
  TOOLTIP: 'z-[160]',
} as const;
